import React, { useState, useRef, useEffect } from 'react';
import { Pause, Play, SkipBack, SkipForward, Volume2, VolumeX, Download, ChevronDown } from 'lucide-react';

interface AudioPlayerProps {
  isPlaying?: boolean;
  setIsPlaying?: (playing: boolean) => void;
  currentTime?: number;
  formatTime?: (seconds: number) => string;
  duration?: string;
  isDarkMode?: boolean;
  audioSrc?: string;
  onTimeUpdate?: (time: number) => void;
  onDurationChange?: (duration: number) => void;
  onDownload?: () => void;
  showDownload?: boolean;
}

export default function AudioPlayer({
  isPlaying: externalIsPlaying,
  setIsPlaying: externalSetIsPlaying,
  currentTime: externalCurrentTime,
  formatTime: externalFormatTime,
  duration: externalDuration,
  isDarkMode = true,
  audioSrc = '',
  onTimeUpdate,
  onDurationChange,
  onDownload,
  showDownload = false
}: AudioPlayerProps) {
  // Internal state management if props not provided
  const [internalIsPlaying, setInternalIsPlaying] = useState(false);
  const [internalCurrentTime, setInternalCurrentTime] = useState(0);
  const [internalDuration, setInternalDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  
  // Use external props if provided, otherwise use internal state
  const isPlaying = externalIsPlaying !== undefined ? externalIsPlaying : internalIsPlaying;
  const setIsPlaying = externalSetIsPlaying || setInternalIsPlaying;
  const currentTime = externalCurrentTime !== undefined ? externalCurrentTime : internalCurrentTime;
  
  // Audio element reference
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const speedDropdownRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<any>(null);
  
  // Format time helper
  const formatTime = externalFormatTime || ((seconds: number): string => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  });
  
  // Calculate duration string
  const durationString = externalDuration || formatTime(internalDuration);
  
  // Update progress bar animation
  const updateProgress = () => {
    if (audioRef.current && !isNaN(audioRef.current.currentTime)) {
      setInternalCurrentTime(audioRef.current.currentTime);
      onTimeUpdate?.(audioRef.current.currentTime);
      
      if (isPlaying) {
        animationRef.current = requestAnimationFrame(updateProgress);
      }
    }
  };
  
  // Handle play/pause state changes
  useEffect(() => {
    if (audioRef.current && !isLoading) {
      if (isPlaying) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              animationRef.current = requestAnimationFrame(updateProgress);
            })
            .catch(err => {
              console.error('Playback error:', err);
              setIsPlaying(false);
            });
        }
      } else {
        audioRef.current.pause();
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      }
    }
  }, [isPlaying, isLoading]);
  
  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);
  
  // Handle playback speed changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);
  
  // Handle metadata loaded
  const handleLoadedMetadata = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration)) {
      setInternalDuration(audioRef.current.duration);
      onDurationChange?.(audioRef.current.duration);
      setIsLoading(false);
    }
  };
  
  // Handle audio can play
  const handleCanPlay = () => {
    setIsLoading(false);
  };
  
  // Handle audio ended
  const handleEnded = () => {
    setIsPlaying(false);
    setInternalCurrentTime(0);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };
  
  // Handle progress bar click
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !audioRef.current || isNaN(internalDuration)) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, clickX / width));
    const newTime = percentage * internalDuration;
    
    if (!isNaN(newTime) && isFinite(newTime)) {
      audioRef.current.currentTime = newTime;
      setInternalCurrentTime(newTime);
      onTimeUpdate?.(newTime);
    }
  };
  
  // Skip backward (10 seconds)
  const handleSkipBack = () => {
    if (audioRef.current && !isNaN(audioRef.current.currentTime)) {
      const newTime = Math.max(0, audioRef.current.currentTime - 10);
      audioRef.current.currentTime = newTime;
      setInternalCurrentTime(newTime);
      onTimeUpdate?.(newTime);
    }
  };
  
  // Skip forward (10 seconds)
  const handleSkipForward = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration)) {
      const newTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 10);
      audioRef.current.currentTime = newTime;
      setInternalCurrentTime(newTime);
      onTimeUpdate?.(newTime);
    }
  };
  
  // Handle volume slider
  const handleVolumeChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newVolume = Math.max(0, Math.min(1, clickX / width));
    
    setVolume(newVolume);
    setIsMuted(false);
  };
  
  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  
  // Handle playback speed change
  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    setShowSpeedOptions(false);
  };
  
  // Available playback speeds
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  
  // Calculate progress percentage safely
  const progressPercentage = (internalDuration > 0 && !isNaN(currentTime) && !isNaN(internalDuration)) 
    ? Math.min(100, (currentTime / internalDuration) * 100) 
    : 0;
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  // Close speed dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (speedDropdownRef.current && !speedDropdownRef.current.contains(event.target as Node)) {
        setShowSpeedOptions(false);
      }
    };

    if (showSpeedOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSpeedOptions]);
  
  return (
    <div className={`border-b ${
      isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
    }`}>
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioSrc || 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onEnded={handleEnded}
        preload="metadata"
        className="hidden"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-4">
          {/* Play/Pause Button */}
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            disabled={isLoading}
            className="p-3 bg-[#1a9f6b] hover:bg-[#148856] text-white rounded-full transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </button>

          {/* Skip Back Button */}
          <button 
            onClick={handleSkipBack}
            disabled={isLoading}
            className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode ? 'hover:bg-[#050a0a] text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
            aria-label="Skip back 10 seconds"
            title="Skip back 10 seconds"
          >
            <SkipBack className="w-4 h-4" />
          </button>

          {/* Skip Forward Button */}
          <button 
            onClick={handleSkipForward}
            disabled={isLoading}
            className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode ? 'hover:bg-[#050a0a] text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
            aria-label="Skip forward 10 seconds"
            title="Skip forward 10 seconds"
          >
            <SkipForward className="w-4 h-4" />
          </button>

          {/* Playback Speed Control */}
          <div className="relative" ref={speedDropdownRef}>
            <button 
              onClick={() => setShowSpeedOptions(!showSpeedOptions)}
              className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1 ${
                isDarkMode ? 'hover:bg-[#050a0a] text-gray-400' : 'hover:bg-gray-100 text-gray-600'
              }`}
              aria-label="Change playback speed"
              title="Change playback speed"
            >
              <span className="text-sm font-medium">
                {playbackSpeed === 1 ? '1x' : `${playbackSpeed}x`}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showSpeedOptions ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Speed Options Dropdown */}
            {showSpeedOptions && (
              <div 
                className={`absolute top-full left-0 mb-2 py-2 rounded-lg shadow-xl z-10 min-w-[80px] ${
                  isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200'
                }`}
              >
                {speedOptions.map((speed) => (
                  <button
                    key={speed}
                    onClick={() => handleSpeedChange(speed)}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-opacity-80 ${
                      playbackSpeed === speed 
                        ? isDarkMode 
                          ? 'bg-[#1a9f6b] text-white' 
                          : 'bg-[#1a9f6b] text-white'
                        : isDarkMode 
                          ? 'text-gray-300 hover:bg-[#1a2424]' 
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {speed === 1 ? '1x' : `${speed}x`}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Progress Bar Section */}
          <div className="flex-1 flex items-center gap-3">
            <span className={`text-sm tabular-nums min-w-[3.5rem] ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
              {formatTime(currentTime)}
            </span>
            
            {/* Progress Bar Container */}
            <div 
              ref={progressBarRef}
              className={`flex-1 h-2 rounded-full cursor-pointer relative group ${
                isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-300'
              }`}
              onClick={handleProgressClick}
            >
              {/* Progress Fill */}
              <div 
                className="absolute top-0 left-0 h-full bg-[#1a9f6b] rounded-full pointer-events-none transition-none"
                style={{ width: `${progressPercentage}%` }}
              />
              
              {/* Hover Effect */}
              <div 
                className="absolute top-0 left-0 h-full bg-[#1a9f6b]/20 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ width: `${progressPercentage}%` }}
              />
              
              {/* Scrubber Handle */}
              <div
                className={`absolute top-1/2 w-4 h-4 bg-white rounded-full shadow-lg transition-opacity pointer-events-none ${
                  isPlaying || progressPercentage > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
                style={{ 
                  left: `${progressPercentage}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              />
            </div>
            
            <span className={`text-sm tabular-nums min-w-[3.5rem] text-right ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
              {durationString}
            </span>
          </div>

          {/* Volume Control */}
          <div className="relative">
            <button 
              onClick={toggleMute}
              onMouseEnter={() => setShowVolumeSlider(true)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-[#050a0a] text-gray-400' : 'hover:bg-gray-100 text-gray-600'
              }`}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
            
            {/* Volume Slider */}
            {showVolumeSlider && (
              <div 
                className={`absolute bottom-full right-0 mb-2 p-3 rounded-lg shadow-xl z-10 ${
                  isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200'
                }`}
                onMouseEnter={() => setShowVolumeSlider(true)}
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <div 
                  className={`w-24 h-2 rounded-full cursor-pointer relative ${
                    isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-300'
                  }`}
                  onClick={handleVolumeChange}
                >
                  <div 
                    className="absolute top-0 left-0 h-full bg-[#1a9f6b] rounded-full pointer-events-none"
                    style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
                  />
                  <div
                    className="absolute top-1/2 w-3 h-3 bg-white rounded-full shadow-md pointer-events-none"
                    style={{ 
                      left: `${(isMuted ? 0 : volume) * 100}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  />
                </div>
                <div className="mt-1 text-xs text-center">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                    {Math.round((isMuted ? 0 : volume) * 100)}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Download Button */}
          {showDownload && onDownload && (
            <button 
              onClick={onDownload}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-[#050a0a] text-gray-400' : 'hover:bg-gray-100 text-gray-600'
              }`}
              aria-label="Download audio file"
              title="Download audio file"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}